import React from 'react';
import {
  Box,
  Popover,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';

import { EmojiEmotions as EmojiIcon } from '@mui/icons-material';
import CustomEmoji from './CustomEmoji';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  disabled?: boolean;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, disabled = false }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'emoji-picker-popover' : undefined;

  const emojiOptions = [
    { emoji: '❤️', label: 'Heart' },
    { emoji: '👍', label: 'Thumbs Up' },
    { emoji: '🎉', label: 'Party' },
    { emoji: '😊', label: 'Smile' },
    { emoji: '🔥', label: 'Fire' },
    { emoji: '👏', label: 'Clap' },
    { emoji: '💯', label: '100' },
    { emoji: '🚀', label: 'Rocket' },
    { emoji: '⭐', label: 'Star' },
    { emoji: '💪', label: 'Muscle' },
    { emoji: '🎯', label: 'Target' },
    { emoji: '✨', label: 'Sparkles' }
  ];

  return (
    <>
      <Tooltip title="Add reaction">
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <EmojiIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 1,
            maxWidth: 300
          }
        }}
      >
        <Paper elevation={0} sx={{ p: 1 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(6, 1fr)', 
            gap: 1, 
            maxWidth: 280 
          }}>
            {emojiOptions.map(({ emoji, label }) => (
              <Box key={emoji}>
                <Tooltip title={label}>
                  <IconButton
                    size="small"
                    onClick={() => handleEmojiClick(emoji)}
                    sx={{
                      p: 0.5,
                      minWidth: 32,
                      minHeight: 32,
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    }}
                  >
                    <CustomEmoji emoji={emoji} size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default EmojiPicker; 